import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { useTranslation } from 'react-i18next';
import { IconButton } from './IconButton.js';
import { Icon } from '../Icon.js';
import { useToast } from './Toast.js';
import { normalizarHtmlPegado, textoPlanoAParrafosHtml, alCopiarTextoEnriquecido } from '../../lib/textoEnriquecido.js';
import {
  esImagenAdmitida, imagenesDelPortapapeles, MAX_BYTES_IMAGEN, resolverImagenesDelHtml, subirImagen, tieneImagenesPorResolver,
} from '../../lib/imagenesPegadas.js';
import './RichTextEditor.css';

export interface RichTextEditorHandle {
  focus(): void;
  getText(): string;
}

export type RichTextEditorToolbarItem =
  | 'bold' | 'italic' | 'underline' | 'strike'
  | 'link'
  | 'bulletList' | 'orderedList'
  | 'blockquote' | 'code'
  | 'divider';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  toolbar?: RichTextEditorToolbarItem[];
  enableHeadings?: boolean;
  enableCodeBlock?: boolean;
}

const DEFAULT_TOOLBAR: RichTextEditorToolbarItem[] = [
  'bold', 'italic', 'underline', 'strike',
  'divider',
  'link',
  'divider',
  'bulletList', 'orderedList',
  'divider',
  'blockquote', 'code',
];

export const RichTextEditor = React.forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value,
      onChange,
      placeholder,
      minHeight = 160,
      toolbar = DEFAULT_TOOLBAR,
      enableHeadings = false,
      enableCodeBlock = false,
    },
    ref
  ) {
    const { t } = useTranslation();
    const toast = useToast();
    const [focused, setFocused] = useState(false);
    const [subiendo, setSubiendo] = useState(0);
    const lastEmitted = useRef(value);
    const editorRef = useRef<Editor | null>(null);

    /** El editor pudo haberse cerrado mientras subía la imagen. */
    function editorVivo(): Editor | null {
      const ed = editorRef.current;
      return ed && !ed.isDestroyed ? ed : null;
    }

    async function pegarImagenes(imagenes: File[]): Promise<void> {
      const admitidas = imagenes.filter(esImagenAdmitida);
      if (admitidas.length === 0) { toast.error(t('composer.image.unsupported')); return; }
      for (const imagen of admitidas) {
        if (imagen.size > MAX_BYTES_IMAGEN) { toast.error(t('composer.image.tooLarge')); continue; }
        setSubiendo(n => n + 1);
        try {
          const src = await subirImagen(imagen);
          editorVivo()?.chain().focus().setImage({ src }).run();
        } catch (err) {
          toast.error(err instanceof Error && err.message ? err.message : t('composer.image.failed'));
        } finally {
          setSubiendo(n => n - 1);
        }
      }
    }

    async function pegarHtmlConImagenes(html: string): Promise<void> {
      setSubiendo(n => n + 1);
      try {
        const resuelto = await resolverImagenesDelHtml(html);
        editorVivo()?.chain().focus().insertContent(normalizarHtmlPegado(resuelto.html)).run();
        if (resuelto.perdidas > 0) toast.warning(t('composer.image.lost', { count: resuelto.perdidas }));
      } finally {
        setSubiendo(n => n - 1);
      }
    }

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: enableHeadings ? {} : false,
          codeBlock: enableCodeBlock ? {} : false,
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
        Placeholder.configure({ placeholder: placeholder ?? '' }),
        // En línea, como en Gmail: el cursor sigue al lado de la imagen. Sin
        // base64: la imagen se sube y el cuerpo solo la nombra.
        Image.configure({ inline: true, allowBase64: false }),
      ],
      content: value,
      // Pegar y copiar como en Word (lib/textoEnriquecido.ts): Enter es un
      // párrafo con su espacio y un salto suelto es un salto de línea. Lo que
      // se pega no trae párrafos vacíos, y lo que se copia se ve afuera como acá.
      editorProps: {
        transformPastedHTML: html => normalizarHtmlPegado(html),
        handlePaste: (_view, event) => {
          const datos = event.clipboardData;
          if (!datos) return false;
          const imagenes = imagenesDelPortapapeles(datos);
          if (imagenes.length > 0) { void pegarImagenes(imagenes); return true; }
          const pegadoHtml = datos.getData('text/html');
          if (tieneImagenesPorResolver(pegadoHtml)) { void pegarHtmlConImagenes(pegadoHtml); return true; }
          // Con HTML, lo ordena transformPastedHTML. Una sola línea: como siempre.
          if (datos.types.includes('text/html')) return false;
          const texto = datos.getData('text/plain');
          if (!/\n/.test(texto)) return false;
          const html = textoPlanoAParrafosHtml(texto);
          // Un solo párrafo entra en línea, donde está el cursor; varios, como párrafos.
          const unSolo = /^<p>((?:(?!<\/?p>).)*)<\/p>$/s.exec(html);
          editorRef.current?.chain().focus().insertContent(unSolo ? unSolo[1]! : html).run();
          return true;
        },
      },
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
      onUpdate({ editor: ed }) {
        const html = ed.getHTML();
        lastEmitted.current = html;
        onChange(html);
      },
    });

    editorRef.current = editor;

    useImperativeHandle(ref, () => ({
      focus() { editor?.commands.focus(); },
      getText() { return editor?.getText() ?? ''; },
    }));

    // Sync external value → editor (e.g. when loading a draft)
    useEffect(() => {
      if (!editor) return;
      if (value === lastEmitted.current) return;
      lastEmitted.current = value;
      editor.commands.setContent(value || '');
    }, [editor, value]);

    if (!editor) return null;

    function handleLink() {
      const prev = (editor.getAttributes('link').href as string | undefined) ?? '';
      const url = window.prompt('URL:', prev || 'https://');
      if (url === null) return;
      if (!url.trim()) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    }

    const borderColor = focused ? 'var(--evo-cyan)' : 'var(--evo-stroke)';

    type BtnCfg = { icon: React.ReactNode; label: string; active: boolean; onClick: () => void };
    const CONFIGS: Record<Exclude<RichTextEditorToolbarItem, 'divider'>, BtnCfg> = {
      bold:        { icon: <strong style={{ fontSize: 13 }}>B</strong>, label: t('composer.format.bold'),         active: editor.isActive('bold'),        onClick: () => editor.chain().focus().toggleBold().run()        },
      italic:      { icon: <em style={{ fontSize: 13 }}>I</em>,         label: t('composer.format.italic'),       active: editor.isActive('italic'),      onClick: () => editor.chain().focus().toggleItalic().run()      },
      underline:   { icon: <u style={{ fontSize: 13 }}>U</u>,           label: t('composer.format.underline'),    active: editor.isActive('underline'),   onClick: () => editor.chain().focus().toggleUnderline().run()   },
      strike:      { icon: <s style={{ fontSize: 13 }}>S</s>,           label: t('composer.format.strike'),       active: editor.isActive('strike'),      onClick: () => editor.chain().focus().toggleStrike().run()      },
      link:        { icon: <Icon name="link" size={14} />,              label: t('composer.format.link'),         active: editor.isActive('link'),        onClick: handleLink                                             },
      bulletList:  { icon: <Icon name="list-bullet" size={14} />,      label: t('composer.format.bullet_list'),  active: editor.isActive('bulletList'),  onClick: () => editor.chain().focus().toggleBulletList().run()  },
      orderedList: { icon: <Icon name="list-ordered" size={14} />,     label: t('composer.format.numbered_list'),active: editor.isActive('orderedList'), onClick: () => editor.chain().focus().toggleOrderedList().run() },
      blockquote:  { icon: <Icon name="blockquote" size={14} />,       label: t('composer.format.blockquote'),   active: editor.isActive('blockquote'),  onClick: () => editor.chain().focus().toggleBlockquote().run()  },
      code:        { icon: <Icon name="code" size={14} />,              label: t('composer.format.code'),         active: editor.isActive('code'),        onClick: () => editor.chain().focus().toggleCode().run()        },
    };

    return (
      <div
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: '6px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          transition: 'border-color 0.15s',
        }}
      >
        <div
          onMouseDown={e => e.preventDefault()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            padding: '4px 8px',
            borderBottom: `1px solid ${borderColor}`,
            background: 'var(--evo-bg)',
            flexShrink: 0,
            flexWrap: 'wrap',
            transition: 'border-color 0.15s',
          }}
        >
          {toolbar.map((item, i) => {
            const key = `${item}-${i}`;
            if (item === 'divider') {
              return (
                <div
                  key={key}
                  style={{ width: '1px', height: '16px', background: 'var(--evo-stroke)', margin: '0 2px', flexShrink: 0 }}
                />
              );
            }
            const cfg = CONFIGS[item];
            return (
              <IconButton
                key={key}
                icon={cfg.icon}
                ariaLabel={cfg.label}
                onClick={cfg.onClick}
                active={cfg.active}
                size="sm"
                variant="ghost"
                tone="muted"
              />
            );
          })}
        </div>

        {subiendo > 0 && (
          <div
            role="status"
            style={{
              padding: 'var(--evo-space-1) var(--evo-space-3)',
              borderBottom: `1px solid ${borderColor}`,
              background: 'var(--evo-bg)',
              color: 'var(--evo-fg-3)',
              fontSize: 'var(--evo-fs-caption)',
              fontFamily: 'var(--evo-font)',
              flexShrink: 0,
            }}
          >
            {t('composer.image.uploading')}
          </div>
        )}

        <div
          className="evo-rte-content"
          style={{
            minHeight,
            padding: '10px 12px',
            overflowY: 'auto',
            background: 'var(--evo-bg)',
            cursor: 'text',
            flex: 1,
          }}
          onClick={() => editor.commands.focus()}
          onCopy={alCopiarTextoEnriquecido}
          onCut={alCopiarTextoEnriquecido}
        >
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }
);
